/**
 * The Map — every objective, and the work standing behind it.
 * Objectives with nothing behind them are the story, so they're marked loudly.
 */

import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ChevronRight, RefreshCw } from 'lucide-react';
import type { ObjectiveNode } from '@/lib/strategyAlignment';
import { getAppDefinition } from '../registry';
import { AppShell, BallLine, ScreenHeading } from '../components/AppShell';
import {
  AppCard,
  EmptyState,
  Eyebrow,
  PrimaryButton,
  SecondaryButton,
  StatusChip,
} from '../components/primitives';
import { useAlignment } from './useAlignment';

function ObjectiveRow({
  node,
  depth,
  titleFor,
}: {
  node: ObjectiveNode;
  depth: number;
  titleFor: (id: string) => string;
}) {
  const backing = node.mappedPriorities;
  return (
    <>
      <div
        className="border-b border-gray-100 px-5 py-3 last:border-0 dark:border-[var(--rule)]"
        style={{ paddingLeft: `${20 + depth * 16}px` }}
      >
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <p className="truncate font-body text-sm font-medium text-gray-900 dark:text-[var(--text-pri)]">
              {node.objective.name}
            </p>
            {backing.length === 0 ? (
              <p className="mt-0.5 font-body text-xs text-luminous-coral-700 dark:text-luminous-coral-300">
                Nothing is working on this
              </p>
            ) : (
              <p className="mt-0.5 truncate font-body text-xs text-gray-500 dark:text-[var(--text-ter)]">
                {backing
                  .slice(0, 3)
                  .map((m) => titleFor(m.priorityId))
                  .join(' · ')}
                {backing.length > 3 ? ` +${backing.length - 3} more` : ''}
              </p>
            )}
          </div>
          {backing.length === 0 ? (
            <StatusChip label="Unbacked" tone="coral" />
          ) : (
            <StatusChip label={`${backing.length} behind it`} tone="green" />
          )}
        </div>
      </div>
      {node.children.map((child) => (
        <ObjectiveRow
          key={child.objective.id}
          node={child}
          depth={depth + 1}
          titleFor={titleFor}
        />
      ))}
    </>
  );
}

export default function AlignMapScreen() {
  const app = getAppDefinition('align')!;
  const navigate = useNavigate();
  const { loading, error, workspaceId, report, tree, titleFor, reload } = useAlignment();

  const score = report?.alignmentScore ?? null;
  const scoreTone = score == null ? 'neutral' : score >= 70 ? 'green' : score >= 40 ? 'mustard' : 'coral';

  return (
    <AppShell
      app={app}
      actions={
        <SecondaryButton onClick={reload} disabled={loading} className="!px-3 !py-1.5">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Reading…' : 'Refresh'}
        </SecondaryButton>
      }
    >
      <ScreenHeading
        title="Does our work match what we said matters?"
        subtitle="Every objective, and the work actually standing behind it."
      />
      <BallLine ballWith="you — look at what's unbacked" />

      <div className="space-y-4">
        <AppCard>
          <Eyebrow>The answer</Eyebrow>
          {!workspaceId ? (
            <p className="mt-2 font-body text-sm text-gray-600 dark:text-[var(--text-sec)]">
              No workspace is selected. Choose one, then come back.
            </p>
          ) : error ? (
            <p className="mt-2 font-body text-sm text-luminous-coral-700 dark:text-luminous-coral-300">
              {error}
            </p>
          ) : loading ? (
            <p className="mt-2 font-body text-sm text-gray-500 dark:text-[var(--text-ter)]">
              Reading the alignment picture…
            </p>
          ) : report && report.totalObjectives === 0 ? (
            <p className="mt-2 font-body text-sm text-gray-600 dark:text-[var(--text-sec)]">
              No score yet. There are no objectives to measure your work against.
            </p>
          ) : report ? (
            <>
              <p className="mt-2 font-heading text-2xl font-semibold text-gray-900 dark:text-[var(--text-pri)]">
                {score}
                <span className="ml-1 align-middle font-body text-sm font-normal text-gray-500 dark:text-[var(--text-ter)]">
                  / 100 aligned
                </span>
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <StatusChip
                  label={`${report.mappedObjectives}/${report.totalObjectives} objectives backed`}
                  tone={scoreTone as 'green' | 'mustard' | 'coral' | 'neutral'}
                />
                <StatusChip
                  label={`${report.mappedPriorities}/${report.totalPriorities} work items mapped`}
                  tone="slate"
                />
                {report.gaps.length > 0 ? (
                  <StatusChip label={`${report.gaps.length} gaps`} tone="coral" />
                ) : null}
              </div>
            </>
          ) : (
            <p className="mt-2 font-body text-sm text-gray-500 dark:text-[var(--text-ter)]">
              No alignment picture yet.
            </p>
          )}
        </AppCard>

        {!workspaceId || error ? null : tree.length === 0 && !loading ? (
          <EmptyState
            title="No objectives set yet"
            hint="Align reads the objectives your workspace defined. Set those first, then come back."
            action={
              <PrimaryButton onClick={() => navigate('/prioritization/strategy/objectives')}>
                Set objectives
              </PrimaryButton>
            }
          />
        ) : (
          <AppCard className="!p-0">
            <div className="px-5 pt-5">
              <Eyebrow>The map</Eyebrow>
            </div>
            <div className="mt-2">
              {tree.map((node) => (
                <ObjectiveRow
                  key={node.objective.id}
                  node={node}
                  depth={0}
                  titleFor={titleFor}
                />
              ))}
            </div>
          </AppCard>
        )}

        <div className="flex justify-end">
          <SecondaryButton onClick={() => navigate('/apps/align/gaps')}>
            <AlertTriangle className="h-4 w-4" /> See the gaps
            <ChevronRight className="h-4 w-4" />
          </SecondaryButton>
        </div>
      </div>
    </AppShell>
  );
}
